import { useQuery } from 'react-query';
import { useAxiosClient } from '../utils/useAxiosClient';

export interface BrandLoveData {
  blove_chart: {
    displayDates: string[];
    dates: string[];
    brands: string[];
    values: number[][];
  };
  post_count_chart?: {
    dates: string[];
    brands: string[];
    values: number[][];
  };
  yearly_metrics?: Record<
    string,
    {
      avg_blove: number;
      avg_engagement: number;
      post_volume: number;
    }
  >;
  date_range_metrics?: Record<
    string,
    {
      avg_blove: number;
      avg_engagement: number;
      post_volume: number;
    }
  >;
  date_range?: {
    start: string;
    end: string;
  };
}

export const useBrandLoveData = (orgId: number, startDate?: string | null, endDate?: string | null, selectedPlatforms?: string[], selectedPillars?: string[]) => {
  const axiosClient = useAxiosClient();

  const queryParams = new URLSearchParams();
  if (startDate) queryParams.set('date_min', startDate);
  if (endDate) queryParams.set('date_max', endDate);
  // Add platforms to query params
  if (selectedPlatforms && selectedPlatforms.length > 0) {
    selectedPlatforms.forEach(p => queryParams.append('platforms', p));
  }
  // Add pillars to query params
  if (selectedPillars && selectedPillars.length > 0) {
    selectedPillars.forEach(p => queryParams.append('pillars', p));
  }

  // Include platforms and pillars in the query key
  const queryKey = ['brandLoveData', orgId, startDate, endDate, selectedPlatforms, selectedPillars];

  // React Query automatically caches by the key
  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery(queryKey, async () => {
    if (!orgId) {
      throw new Error('No organization selected');
    }

    // Build the final URL using the pre-constructed queryParams
    const url = `/api/brand-love?org=${orgId}&${queryParams.toString()}`;
    
    // Use the correctly constructed URL
    const response = await axiosClient.get<BrandLoveData>(url); 

    // If the response has an 'error' field, throw an error
    if (response.data && typeof response.data === 'object' && 'error' in response.data) {
      throw new Error(
        typeof response.data.error === 'string'
          ? response.data.error
          : 'An error occurred'
      );
    }

    return response.data;
  }, {
    enabled: !!orgId,
    staleTime: 0, // Data stale immediately → triggers background refetch
    cacheTime: 30 * 60 * 1000, // 30 minutes max cache lifetime
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    // Handle null/undefined data
    select: (data) => data ?? null,
  });

  // Return your existing pattern:
  // Use isFetching instead of isLoading to show loading state during refetches
  // isLoading = true only when no cached data AND fetching
  // isFetching = true whenever fetching (including background refetch with keepPreviousData)
  return {
    data: data ?? null,
    loading: isFetching,
    error: error ? (error as Error).message : null,
  };
}
